
import { apiSignIn, apiSignOut, apiSignUp } from '@/services/AuthService'
import {
    setUser,
    signInSuccess,
    signOutSuccess,
    useAppSelector,
    useAppDispatch,
    type Role,
    type UserState,
} from '@/store'
import appConfig from '@/configs/app.config'
import { REDIRECT_URL_KEY } from '@/constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import type { SignInCredential, SignInResponse, SignUpCredential } from '@/types/auth'

type Status = 'success' | 'failed'

const mapAuthUserToState = (user: SignInResponse['user']): UserState => ({
    avatar: user.avatar ?? '',
    userName: user.userName ?? user.email ?? '',
    name: user.name ?? '',
    authority: user.authority ?? ['USER'],
    roles: user.roles as Role[],
    email: user.email ?? '',
    domains: user.domains as UserState['domains'],
    token: user.token,
    id: user.id,
})

function useAuth() {
    const dispatch = useAppDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const { token, signedIn } = useAppSelector((state) => state.auth.session)

    const signIn = async ( values: SignInCredential ) : 
      Promise<
        | {
              status: Status
              message: string
          }
        | undefined
    > => {
        try {
            const resp = await apiSignIn(values)
            if (resp.data) {
                const { token } = resp.data.user
                dispatch(signInSuccess(token))
                if (resp.data.user) {
                    dispatch(setUser(mapAuthUserToState(resp.data.user)))
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        } catch (errors: any) {
            return {
                status: 'failed',
                message: errors?.response?.data || errors?.response?.data?.message || errors?.toString(),
            }
        }
    }

    const signUp = async (values: SignUpCredential) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = (resp.data ?? '');
                dispatch(signInSuccess(token ?? ''))
                if (resp.data.user) {
                    dispatch(setUser(mapAuthUserToState(resp.data.user)))
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }

            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        } catch (errors: any) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(signOutSuccess())
        dispatch(
            setUser({
                avatar: '', //starter
                userName: 'Anonymous', //starter
                name: '',
                authority: [], //starter
                roles: [],
                email: '',
                domains: [],
                token: '',
            })
        )
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        // await apiSignOut()//for now, we dont need an api call to signout
        handleSignOut()
    }

    return {
        authenticated: token && signedIn,
        signIn,
        signUp,
        signOut,
    }
}

export default useAuth
